import { Link } from '@remix-run/react';
import { generateFaqJsonLd } from '~/utils/seo';

export interface FAQItem {
  question: string;
  answer: string;
}

interface FAQProps {
  /** The questions and answers to render. */
  items: FAQItem[];
  /** Section heading. Pass null to hide it. */
  title?: string | null;
  /** Optional supporting line under the heading. */
  subtitle?: string;
  /** Anchor id for the section (e.g. "faq"). */
  id?: string;
  /** Classes for the outer <section> (background, vertical padding, margins). */
  className?: string;
  /** Classes for the inner container (max width, horizontal padding). */
  innerClassName?: string;
  /** Heading alignment. */
  align?: 'left' | 'center';
  /** Inject FAQPage JSON-LD. Set false when the page already outputs its own. */
  includeSchema?: boolean;
  /** Show a "Still have a question?" link to /contact. */
  showContactCta?: boolean;
}

/**
 * Consistent, accessible FAQ accordion used across the site. Renders an
 * expand/collapse list and, by default, the matching FAQPage structured data.
 */
export default function FAQ({
  items,
  title = 'Frequently Asked Questions',
  subtitle,
  id,
  className = 'py-16 bg-white',
  innerClassName = 'max-w-3xl mx-auto px-4 sm:px-6 lg:px-8',
  align = 'center',
  includeSchema = true,
  showContactCta = false,
}: FAQProps) {
  if (!items || items.length === 0) return null;

  const faqJsonLd = generateFaqJsonLd(items);

  return (
    <section className={className} id={id}>
      {includeSchema && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
        />
      )}
      <div className={innerClassName}>
        {title !== null && (
          <div className={align === 'center' ? 'text-center mb-10' : 'mb-8'}>
            <h2 className="text-2xl lg:text-3xl font-bold text-gray-900">{title}</h2>
            {subtitle && <p className="mt-3 text-lg text-gray-600">{subtitle}</p>}
          </div>
        )}

        <div className="divide-y divide-gray-100 border-y border-gray-100">
          {items.map((item) => (
            <details key={item.question} className="group py-5 [&_summary]:list-none">
              <summary className="flex items-start justify-between gap-4 cursor-pointer font-semibold text-gray-900">
                <span>{item.question}</span>
                <span className="ml-4 shrink-0 text-gray-400 transition-transform group-open:rotate-45 text-2xl leading-none">
                  +
                </span>
              </summary>
              <p className="mt-3 text-gray-600 leading-relaxed">{item.answer}</p>
            </details>
          ))}
        </div>

        {showContactCta && (
          <p className="mt-8 text-center text-gray-600">
            Still have a question?{' '}
            <Link
              to="/contact"
              className="font-medium text-gray-900 underline underline-offset-2 hover:text-black"
            >
              Get in touch
            </Link>
          </p>
        )}
      </div>
    </section>
  );
}
